import { CommonModule, Location } from '@angular/common';
import { Component, OnInit, Input, DoCheck } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { IonicModule, ModalController, NavController, AlertController, PopoverController } from '@ionic/angular';
import _ from 'lodash';
import { LucideAngularModule } from 'lucide-angular';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AvatarModule } from 'src/app/components/avatar/avatar.module';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { CollectionSelectorComponent } from 'src/app/components/collection-selector/collection-selector.component';
import { IconPickerComponent } from 'src/app/components/icon-picker/icon-picker.component';
import { ImageModalComponent } from 'src/app/components/image-modal/image-modal.component';
import { SearchablePopoverComponent } from 'src/app/components/searchable-popover/searchable-popover.component';
import { PROJECT_MEMBER_ROLES } from 'src/app/constants';
import { fileNameFromImageModalUpload } from 'src/app/utils/utils';
import { ICON_MAP, getIconComponent } from 'src/constants/icon-map.constants';
import { environment } from 'src/environments/environment';
import { Tenant, SlackChannel } from 'src/models';
import { Portal, PortalFormModel } from 'src/models/portals';
import { Project, ProjectMember } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { KnowledgeBaseCollectionsService } from 'src/services/knowledge-base-collections.service';
import { MessageService } from 'src/services/message.service';
import { PortalsService } from 'src/services/portals.service';
import { ProjectsService } from 'src/services/projects.service';
import { SlackService } from 'src/services/slack.service';
import { TenantsService } from 'src/services/tenants.service';
import { TicketTypesService } from 'src/services/ticket-types.service';
import { UploadService } from 'src/services/upload.service';

export interface PortalConfig {
  isExternal: boolean;
  domainSuffix: string;
  title: string;
  noPortalMessage: string;
  noPortalDescription: string;
}

// Portal default domain prefixes (matching backend constants)
export const PORTAL_DEFAULT_DOMAIN_PREFIXES = {
  INTERNAL: 'portal',
  EXTERNAL: 'help',
} as const;

@Component({
  standalone: true,
  imports: [
    AvatarModule,
    CommonModule,
    FormsModule,
    IonicModule,
    SharedPipesModule,
    NgxTippyModule,
    ChannelSelectorModule,
    IconPickerComponent,
    CollectionSelectorComponent,
    LucideAngularModule,
  ],
  selector: 'app-portals-base',
  templateUrl: './portals-base.component.html',
  styleUrls: ['./portals-base.component.scss'],
})
export class PortalsBaseComponent implements OnInit, DoCheck {
  @Input() config!: PortalConfig;

  portal: Partial<Portal> | null = null;
  projects: Partial<Project>[] = [];
  availableCollections: any[] = [];
  parentCollections: any[] = []; // Only top-level collections (parentId === null)
  isCreatingPortal: boolean = false;
  tenant: Tenant | null = null;
  selectedSlackChannel: SlackChannel | null = null;
  selectedImpersonateUser: any = null;
  impersonateEmail: string = '';

  // Portal documentation URL
  portalDocsUrl = 'https://docs.unthread.io/portal/portal-overview';
  rootDomain = environment.rootDomain;

  // Use shared icon map
  iconMap = ICON_MAP;

  // Get the icon component for a given icon name
  getIconComponent(iconName: string) {
    return getIconComponent(iconName);
  }

  // Tooltip content
  tooltips = {
    customDomain: `The domain where your portal will be accessible (e.g., support.${this.tenant?.subdomain || 'yourcompany'}.com).`,
    signInEmailSender:
      'The email address used for sending magic link emails (e.g., support@yourcompany.com). The domain must be verified in your email domain settings.',
    logo: 'The logo that will be used for branding the customer portal',
    status: 'Live portals are accessible to customers or employees',
    primaryColor: 'The main color theme used throughout the portal interface',
    darkThemeAccentColor: 'The accent color used when in dark mode. We recommend using a light color.',
    projects:
      'Knowledge bases and other resources associated with these projects will be accessible via this customer portal',
    featuredCollections:
      'Select which knowledge base collections should be prominently featured on the portal homepage',
    customLinks: 'Add custom navigation links to external resources or important pages',
    allowDirectTicketCreation: 'Allow users to create support tickets via a form directly from the portal',
    slackChannelId:
      "By default, we'll post details to the Project's default triage channel, and we'll use this channel as a backup if none is set",
    homepageMetadata:
      'Customize the title and description that appear on your portal homepage for better SEO and user experience',
    loginMethods:
      'Configure how users will authenticate to access your portal. You can enable Magic Link (email verification) and/or Single Sign-On (SSO).',
  };

  portalForm: PortalFormModel = {
    customDomain: '',
    signInEmailSender: '',
    primaryColor: '#000000',
    darkThemeAccentColor: '#ffffff',
    selectedProjectIds: [],
    supportLogo: null,
    isEnabled: false,
    allowDirectTicketCreation: true,
    slackChannelId: '',
    customLinks: [],
    featuredCollections: [],
    collections: [],
    homepageMetadata: {
      title: '',
      description: '',
    },
    loginMethods: [
      { type: 'magicLink', enabled: true },
      {
        type: 'sso',
        enabled: false,
        sso: {
          name: '',
          clientId: '',
          issuerUrl: '',
          clientSecret: '',
        },
      },
    ],
  };

  // Track original form state for change detection
  originalPortalForm: PortalFormModel | null = null;
  originalSlackChannel: SlackChannel | null = null;

  // Track if top card (logo, color, etc.) has been changed from original
  get hasTopCardChanged(): boolean {
    if (!this.originalPortalForm) return false;

    // Create copies of the arrays before sorting to avoid mutating the original arrays
    const currentProjectIds = [...this.portalForm.selectedProjectIds].sort();
    const originalProjectIds = [...this.originalPortalForm.selectedProjectIds].sort();

    const hasLoginMethodsChanged =
      JSON.stringify(this.portalForm.loginMethods) !== JSON.stringify(this.originalPortalForm.loginMethods);

    return (
      this.portalForm.primaryColor !== this.originalPortalForm.primaryColor ||
      this.portalForm.darkThemeAccentColor !== this.originalPortalForm.darkThemeAccentColor ||
      this.portalForm.supportLogo !== this.originalPortalForm.supportLogo ||
      JSON.stringify(currentProjectIds) !== JSON.stringify(originalProjectIds) ||
      this.portalForm.isEnabled !== this.originalPortalForm.isEnabled ||
      this.portalForm.allowDirectTicketCreation !== this.originalPortalForm.allowDirectTicketCreation ||
      this.portalForm.slackChannelId !== this.originalPortalForm.slackChannelId ||
      hasLoginMethodsChanged
    );
  }

  // Track if portal customization section has been changed from original
  get hasPortalCustomizationChanged(): boolean {
    if (!this.originalPortalForm) return false;

    // Check featured collections changes (compare arrays directly to detect order changes)
    const currentFeaturedCollections = JSON.stringify(this.portalForm.featuredCollections);
    const originalFeaturedCollections = JSON.stringify(this.originalPortalForm.featuredCollections);

    // Check collections changes (compare arrays directly to detect order changes)
    const currentOrderedCollections = JSON.stringify(this.portalForm.collections);
    const originalOrderedCollections = JSON.stringify(this.originalPortalForm.collections);

    // Check custom links changes (deep comparison for objects)
    const currentCustomLinks = JSON.stringify(this.portalForm.customLinks);
    const originalCustomLinks = JSON.stringify(this.originalPortalForm.customLinks);

    const hasChanges =
      this.portalForm.primaryColor !== this.originalPortalForm.primaryColor ||
      this.portalForm.darkThemeAccentColor !== this.originalPortalForm.darkThemeAccentColor ||
      currentFeaturedCollections !== originalFeaturedCollections ||
      currentOrderedCollections !== originalOrderedCollections ||
      currentCustomLinks !== originalCustomLinks ||
      this.portalForm.customDomain !== this.originalPortalForm.customDomain ||
      JSON.stringify(this.portalForm.homepageMetadata) !== JSON.stringify(this.originalPortalForm.homepageMetadata);

    return hasChanges;
  }

  // Track if authentication settings have been changed
  get hasAuthenticationChanged(): boolean {
    if (!this.originalPortalForm) return false;
    const loginMethodsChanged =
      JSON.stringify(this.portalForm.loginMethods) !== JSON.stringify(this.originalPortalForm.loginMethods);
    const signInEmailSenderChanged = this.portalForm.signInEmailSender !== this.originalPortalForm.signInEmailSender;
    return loginMethodsChanged || signInEmailSenderChanged;
  }

  // Track if the form has any changes (for overall save button state)
  get hasFormChanges(): boolean {
    return this.hasTopCardChanged || this.hasPortalCustomizationChanged;
  }

  // Track unsaved changes toast
  private unsavedChangesToast: any = null;
  private lastHasChangesState: boolean = false;

  // Angular change detection - optimized to only act when state changes
  ngDoCheck() {
    const currentHasChanges = this.hasPortalCustomizationChanged;

    // Only act if the state has actually changed
    if (currentHasChanges !== this.lastHasChangesState) {
      this.lastHasChangesState = currentHasChanges;

      if (currentHasChanges && !this.isInitialLoading && !this.isCreatingPortal && !this.unsavedChangesToast) {
        this.showUnsavedChangesToast();
      } else if (!currentHasChanges && this.unsavedChangesToast) {
        this.dismissUnsavedChangesToast();
      }
    }
  }

  // Show unsaved changes toast
  private async showUnsavedChangesToast() {
    if (this.unsavedChangesToast) {
      return; // Toast already showing
    }

    this.unsavedChangesToast = await this.msgSrvc.show('Unsaved changes', {
      duration: 0, // Don't auto-dismiss
      position: 'top',
      buttons: [
        {
          text: 'Discard',
          role: 'cancel',
          handler: () => {
            this.revertPortalCustomizationChanges();
            this.dismissUnsavedChangesToast();
          },
        },
        {
          text: 'Save',
          handler: () => {
            this.submitPortalForm();
            this.dismissUnsavedChangesToast();
          },
        },
      ],
    });
  }

  // Dismiss unsaved changes toast
  private dismissUnsavedChangesToast() {
    if (this.unsavedChangesToast) {
      this.unsavedChangesToast.dismiss();
      this.unsavedChangesToast = null;
    }
  }

  // Track manual collections order toggle state
  private _isManualCollectionsOrderEnabled: boolean = false;

  get isManualCollectionsOrderEnabled(): boolean {
    return this._isManualCollectionsOrderEnabled;
  }

  set isManualCollectionsOrderEnabled(value: boolean) {
    this._isManualCollectionsOrderEnabled = value;
  }

  // Track projects without ticket types for warning display
  projectsWithoutTicketTypes: { id: string; name: string }[] = [];

  // Valid email domains for validation
  verifiedEmailDomains: any[] = [];

  // Email input components
  signInEmailPrefix: string = '';
  selectedEmailDomain: string = '';

  showSsoSecret = false; // Toggle for showing/hiding SSO client secret

  isLoading = false;
  isInitialLoading = true; // Track initial loading state
  isUploadingLogo = false; // Track logo upload loading state
  isDnsTesting = false; // Track DNS test loading state
  isTlsTesting = false; // Track TLS test loading state
  isPublishing = false; // Track portal publishing loading state
  isSavingAuthentication = false; // Track authentication save loading state
  dnsTestSteps = [
    'Resolving domain...',
    'Checking CNAME records...',
    'Verifying DNS configuration...',
    'Testing connection...',
  ];
  tlsTestSteps = ['Checking certificate status...', 'Verifying SSL configuration...', 'Validating certificate...'];
  publishSteps = [
    'Checking ticket types...',
    'Validating project configuration...',
    'Verifying portal requirements...',
    'Testing DNS configuration...',
    'Checking TLS certificate...',
    'Preparing to publish...',
  ];
  currentDnsStep = 0;
  currentTlsStep = 0;
  currentPublishStep = 0;
  currentTestingPortal: Portal | null = null;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private portalsService: PortalsService,
    private msgSrvc: MessageService,
    private projectsService: ProjectsService,
    private tenantsService: TenantsService,
    private uploadService: UploadService,
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private alertCtrl: AlertController,
    private ticketTypesService: TicketTypesService,
    private slackService: SlackService,
    private collectionsService: KnowledgeBaseCollectionsService,
    private popoverCtrl: PopoverController,
  ) {}

  async ngOnInit() {
    await Promise.all([this.getProjects(), this.getTenant(), this.loadEmailDomains()]);

    // get portal data after other data is loaded
    await this.getPortal();

    // Automatically start portal creation if no portal exists
    // Only do this after initial loading is complete
    if (!this.portal) {
      await this.startCreatePortal();
    }

    // Now fetch collections after portal data is available
    await this.getCollections();
  }

  async getTenant() {
    try {
      this.tenant = await this.tenantsService.getTenant();
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  onSlackChannelChanged(channel: SlackChannel | undefined) {
    this.selectedSlackChannel = channel || null;
    this.portalForm.slackChannelId = channel?.id || '';
  }

  async getPortal() {
    try {
      const portalResponse = await this.portalsService.list({
        limit: null,
        descending: false,
        where: [
          {
            field: 'isExternal',
            operator: '==',
            value: this.config.isExternal,
          },
        ],
      });

      this.portal = portalResponse.data.length > 0 ? portalResponse.data[0] : null;

      // Initialize form with existing portal data if portal exists
      if (this.portal) {
        await this.initializePortalForm(this.portal);
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isInitialLoading = false;
    }
  }

  private async initializePortalForm(portal: Partial<Portal>) {
    // Create a new form object to ensure change detection picks up the updates
    const newForm: PortalFormModel = {
      customDomain: portal.customDomain || '',
      signInEmailSender: portal.signInEmailSender || '',
      primaryColor: portal.settings?.primaryColor || '#000000',
      darkThemeAccentColor: portal.settings?.darkThemeAccentColor || '#ffffff',
      selectedProjectIds: [...(portal.projectIds || [])], // Create a copy of the array
      supportLogo: portal.settings?.logoUrl || null,
      isEnabled: portal.isEnabled || false,
      allowDirectTicketCreation: portal.settings?.allowDirectTicketCreation ?? true,
      slackChannelId: portal.slackChannelId || '',
      customLinks: portal.customLinks || [],
      featuredCollections: portal.featuredCollections || [],
      collections: portal.collections || [],
      homepageMetadata: portal.homepageMetadata || {
        title: '',
        description: '',
      },
      loginMethods: this.initializeLoginMethods(portal.loginMethods),
    };

    // Initialize toggle state based on collections
    this._isManualCollectionsOrderEnabled = (portal.collections && portal.collections.length > 0) || false;

    // Update the form with the new data
    this.portalForm = newForm;

    // Initialize email components from the form data (use setTimeout to ensure form is updated)
    if (portal.signInEmailSender?.trim()) {
      const email = portal.signInEmailSender.trim();
      const atIndex = email.indexOf('@');
      if (atIndex > 0) {
        this.signInEmailPrefix = email.substring(0, atIndex);
        this.selectedEmailDomain = email.substring(atIndex + 1);
      }
    } else {
      this.signInEmailPrefix = '';
      this.selectedEmailDomain = '';
    }

    // Set the selected Slack channel if we have one
    if (newForm.slackChannelId) {
      // Backend now provides slackChannel details through the association
      // If no channel details are available, create a minimal object
      if (!this.portal?.slackChannel) {
        this.selectedSlackChannel = {
          id: newForm.slackChannelId,
          name: 'Channel not found',
          displayName: 'Channel not found',
        } as SlackChannel;
      } else {
        this.selectedSlackChannel = this.portal.slackChannel;
        this.originalSlackChannel = this.portal.slackChannel;
      }
    }

    // Store the original state for change detection - create deep copies of arrays
    this.originalPortalForm = {
      ...newForm,
      selectedProjectIds: [...newForm.selectedProjectIds], // Create a copy of the array
      customLinks: [...newForm.customLinks], // Create a copy of the array
      featuredCollections: [...newForm.featuredCollections], // Create a copy of the array
      collections: [...newForm.collections], // Create a copy of the array
      homepageMetadata: newForm.homepageMetadata ? { ...newForm.homepageMetadata } : { title: '', description: '' }, // Create a copy of the object
      loginMethods: newForm.loginMethods ? JSON.parse(JSON.stringify(newForm.loginMethods)) : undefined,
    };

    // Initialize projects without ticket types warning for ALL projects, not just selected ones
    if (this.projects.length > 0) {
      const allProjectIds = this.projects.map((project) => project.id || '').filter((id) => id);
      this.projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(allProjectIds);
    }

    // Update the precalculated featured collections
    this.updateFeaturedCollections();
  }

  // Restore the original form state
  restoreOriginalForm() {
    if (this.originalPortalForm) {
      this.portalForm = {
        ...this.originalPortalForm,
        selectedProjectIds: [...this.originalPortalForm.selectedProjectIds],
        customLinks: [...this.originalPortalForm.customLinks],
        featuredCollections: [...this.originalPortalForm.featuredCollections],
        collections: [...this.originalPortalForm.collections],
        homepageMetadata: this.originalPortalForm.homepageMetadata
          ? { ...this.originalPortalForm.homepageMetadata }
          : { title: '', description: '' },
        loginMethods: this.originalPortalForm.loginMethods
          ? JSON.parse(JSON.stringify(this.originalPortalForm.loginMethods))
          : undefined,
      };
      this.selectedSlackChannel = this.originalSlackChannel;
      this._isManualCollectionsOrderEnabled =
        (this.originalPortalForm.collections && this.originalPortalForm.collections.length > 0) || false;
      this.updateFeaturedCollections();
    }
  }

  async getProjects() {
    try {
      if (this.authService.isTenantAdmin) {
        const projectsResponse = await this.projectsService.list({
          where: [
            {
              field: 'tenantId',
              operator: '==',
              value: this.authService.tenantId,
            },
            // Filter projects based on portal type
            {
              field: 'isExternal',
              operator: '==',
              value: this.config.isExternal,
            },
            // Filter out deleted projects
            {
              field: 'deletedAt',
              operator: '==',
              value: null,
            },
          ],
        });

        this.projects = projectsResponse.data;
      } else {
        const projectsResponse = await this.projectsService.listForUser(this.authService.userId, {
          limit: null,
          order: ['project.name'],
          descending: false,
          where: [
            {
              field: 'role',
              operator: '==',
              value: PROJECT_MEMBER_ROLES.OWNER,
            },
            // Filter projects based on portal type
            {
              field: 'project.isExternal',
              operator: '==',
              value: this.config.isExternal,
            },
            // Filter out deleted projects
            {
              field: 'project.deletedAt',
              operator: '==',
              value: null,
            },
          ],
        });

        this.projects = projectsResponse.data.map(
          (item: Partial<ProjectMember> & { project?: Project }) => item.project!,
        );
      }

      // Check for projects without ticket types when projects are loaded
      if (this.projects.length > 0) {
        const allProjectIds = this.projects.map((project) => project.id || '').filter((id) => id);
        this.projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(allProjectIds);
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  async getCollections() {
    try {
      // Only fetch collections if we have a portal (existing or being created)
      if (!this.portal && !this.isCreatingPortal) {
        this.availableCollections = [];
        return;
      }

      // Get the project IDs to filter collections by
      const projectIds = this.portal?.projectIds || this.portalForm.selectedProjectIds;

      // If no projects are selected, don't fetch collections
      if (!projectIds || projectIds.length === 0) {
        this.availableCollections = [];
        this.updateFeaturedCollections();
        return;
      }

      // Use the general collections API with project filtering
      if (this.portal?.id || this.isCreatingPortal) {
        // For existing portal or portal being created, filter collections by selected projects
        // Include both project-specific collections and shared collections (projectId=null)

        // Make two separate API calls since frontend doesn't support OR conditions
        const [projectCollectionsResponse, sharedCollectionsResponse] = await Promise.all([
          // Get project-specific collections
          this.collectionsService.list({
            limit: 1000,
            where: [
              {
                field: 'projectId',
                operator: 'in',
                value: projectIds,
              },
            ],
          }),
          // Get shared collections (projectId=null)
          this.collectionsService.list({
            limit: 1000,
            where: [
              {
                field: 'projectId',
                operator: 'is',
                value: null,
              },
            ],
          }),
        ]);

        // Combine the results
        const projectCollections = projectCollectionsResponse.data || [];
        const sharedCollections = sharedCollectionsResponse.data || [];
        this.availableCollections = [...projectCollections, ...sharedCollections];

        // Create separate array for parent collections only (for collections ordering)
        this.parentCollections = this.availableCollections.filter((collection) => collection.parentId === null);
      } else {
        // For new portal, we'll get collections after projects are selected
        // This will be called again when projects change
        this.availableCollections = [];
        this.parentCollections = [];
      }

      // Update the precalculated featured collections after collections are loaded
      this.updateFeaturedCollections();
    } catch (err) {
      this.msgSrvc.showError(err);
      this.availableCollections = [];
      this.parentCollections = [];
    }
  }

  async showCollectionSelector() {
    const popover = await this.popoverCtrl.create({
      component: CollectionSelectorComponent,
      cssClass: 'collection-selector-popover',
      componentProps: {
        title: 'Choose Collection',
        showRootOption: false,
        onlyShowCollectionsWithArticles: false,
        preFilteredCollections: this.availableCollections,
        callback: (selectedId: string | null) => {
          if (selectedId && !this.portalForm.featuredCollections.includes(selectedId)) {
            this.portalForm.featuredCollections.push(selectedId);
            // Update the precalculated featured collections
            this.updateFeaturedCollections();
          }
        },
      },
    });

    await popover.present();
  }

  removeFeaturedCollection(collectionId: string) {
    const index = this.portalForm.featuredCollections.indexOf(collectionId);
    if (index > -1) {
      this.portalForm.featuredCollections.splice(index, 1);
      // Update the precalculated featured collections
      this.updateFeaturedCollections();
    }
  }

  // Precalculated featured collections to avoid calling the method in ngFor
  private _featuredCollections: { id: string; name: string; projectName: string }[] = [];

  // Getter for featured collections that returns the precalculated value
  get featuredCollections(): { id: string; name: string; projectName: string }[] {
    return this._featuredCollections;
  }

  // Method to update the precalculated featured collections
  private updateFeaturedCollections(): void {
    this._featuredCollections = this.portalForm.featuredCollections
      .map((collectionId) => {
        const collection = this.availableCollections.find((c) => c.id === collectionId);
        if (collection) {
          return {
            id: collection.id,
            name: collection.name,
            projectName: collection.projectId ? this.getProjectName(collection.projectId) : 'Shared Collections',
          };
        }
        return null;
      })
      .filter(Boolean) as { id: string; name: string; projectName: string }[];
  }

  // Get featured collections as a flat list with project names (kept for backward compatibility)
  getFeaturedCollections(): { id: string; name: string; projectName: string }[] {
    return this.featuredCollections;
  }

  // Helper method to get collection name by ID
  getCollectionName(collectionId: string): string {
    const collection = this.availableCollections.find((c) => c.id === collectionId);
    return collection ? collection.name : 'Unknown Collection';
  }

  // Helper method to get collection project name by ID
  getCollectionProjectName(collectionId: string): string {
    const collection = this.availableCollections.find((c) => c.id === collectionId);
    if (collection && collection.projectId) {
      return this.getProjectName(collection.projectId);
    }
    return 'Shared Collections';
  }

  // TrackBy function for ngFor to help Angular track items properly
  trackByCollectionId(index: number, collectionId: string): string {
    return collectionId;
  }

  // Handle reordering of featured collections
  onReorder(event: any) {
    const { from, to } = event.detail;

    // Create a copy of the array to avoid mutation issues
    const newArray = [...this.portalForm.featuredCollections];

    // Move the item in the array
    const itemToMove = newArray.splice(from, 1)[0];
    newArray.splice(to, 0, itemToMove);

    // Update the portalForm array
    this.portalForm.featuredCollections = newArray;

    // Update the precalculated featured collections
    this.updateFeaturedCollections();

    // Complete the reorder
    event.detail.complete();
  }

  // Handle reordering of collections
  onCollectionsReorder(event: any) {
    const { from, to } = event.detail;

    // Create a copy of the array to avoid mutation issues
    const newArray = [...this.portalForm.collections];

    // Move the item in the array
    const itemToMove = newArray.splice(from, 1)[0];
    newArray.splice(to, 0, itemToMove);

    // Update the portalForm array
    this.portalForm.collections = newArray;

    // Complete the reorder
    event.detail.complete();
  }

  // Show collections selector for ordering
  async showCollectionsOrderSelector() {
    const popover = await this.popoverCtrl.create({
      component: CollectionSelectorComponent,
      cssClass: 'collection-selector-popover',
      componentProps: {
        title: 'Choose Collections to Order',
        subtitle: 'Select collections to define their order in the documentation page',
        showRootOption: false,
        onlyShowCollectionsWithArticles: false,
        availableCollections: this.parentCollections,
        selectedCollectionIds: this.portalForm.collections,
        showProjectNames: true,
        callback: (selectedId: string | null) => {
          if (selectedId && !this.portalForm.collections.includes(selectedId)) {
            this.portalForm.collections.push(selectedId);
          }
        },
      },
    });

    await popover.present();
  }

  // Remove collection from order
  removeCollectionFromOrder(collectionId: string) {
    const index = this.portalForm.collections.indexOf(collectionId);
    if (index > -1) {
      this.portalForm.collections.splice(index, 1);
    }
  }

  // Track by collection ID for ngFor
  trackByCollectionIdForOrder(index: number, collectionId: string): string {
    return collectionId;
  }

  // Handle manual collections order toggle
  onManualCollectionsOrderToggle() {
    if (this._isManualCollectionsOrderEnabled) {
      // Toggling ON: populate with parent collections only in alphabetical order
      if (this.parentCollections && this.parentCollections.length > 0) {
        const sortedCollections = [...this.parentCollections]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((collection) => collection.id);
        this.portalForm.collections = sortedCollections;
      }
    } else {
      // Toggling OFF: clear collections array
      this.portalForm.collections = [];
    }
  }

  // Revert only portal customization changes (featured collections and custom links)
  revertPortalCustomizationChanges() {
    if (!this.originalPortalForm) return;

    // Revert color changes
    this.portalForm.primaryColor = this.originalPortalForm.primaryColor;
    this.portalForm.darkThemeAccentColor = this.originalPortalForm.darkThemeAccentColor;

    // Revert featured collections
    this.portalForm.featuredCollections = [...this.originalPortalForm.featuredCollections];

    // Revert collections
    this.portalForm.collections = [...this.originalPortalForm.collections];

    // Update toggle state based on reverted collections
    this._isManualCollectionsOrderEnabled =
      (this.originalPortalForm.collections && this.originalPortalForm.collections.length > 0) || false;

    // Revert custom links (deep copy to avoid reference issues)
    this.portalForm.customLinks = this.originalPortalForm.customLinks.map((link) => ({ ...link }));

    // Revert custom domain if it was cleared but not saved
    this.portalForm.customDomain = this.originalPortalForm.customDomain;

    // Revert homepage metadata
    this.portalForm.homepageMetadata = this.originalPortalForm.homepageMetadata
      ? { ...this.originalPortalForm.homepageMetadata }
      : { title: '', description: '' };

    // Revert login methods
    this.portalForm.loginMethods = this.originalPortalForm.loginMethods
      ? JSON.parse(JSON.stringify(this.originalPortalForm.loginMethods))
      : undefined;

    // Update the precalculated featured collections
    this.updateFeaturedCollections();

    // Dismiss unsaved changes toast since changes are reverted
    this.dismissUnsavedChangesToast();
  }

  async startCreatePortal() {
    this.isCreatingPortal = true;

    // Get default logo from tenant (supportLogo takes precedence over logo)
    const defaultLogo = this.tenant?.supportLogo || this.tenant?.logo || null;

    // Get default primary color from tenant
    const defaultPrimaryColor = this.tenant?.primaryColor || '#000000';

    // Get all project IDs for the current portal type
    const allProjectIds = this.projects.map((project) => project.id || '').filter((id) => id);

    // Initialize projects without ticket types warning first
    this.projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(allProjectIds);

    // Only select projects that have ticket types
    const projectsWithTicketTypes = allProjectIds.filter(
      (projectId) => !this.projectsWithoutTicketTypes.some((p) => p.id === projectId),
    );

    this.portalForm = {
      customDomain: '',
      signInEmailSender: '',
      primaryColor: defaultPrimaryColor,
      darkThemeAccentColor: '#ffffff',
      selectedProjectIds: projectsWithTicketTypes,
      supportLogo: defaultLogo,
      isEnabled: false,
      allowDirectTicketCreation: true,
      slackChannelId: '',
      customLinks: [],
      featuredCollections: [],
      collections: [],
      loginMethods: [
        { type: 'magicLink', enabled: true },
        {
          type: 'sso',
          enabled: false,
          sso: {
            name: '',
            clientId: '',
            issuerUrl: '',
            clientSecret: '',
          },
        },
      ],
    };

    // Reset the selected Slack channel to null
    this.selectedSlackChannel = null;
    // Store the original state for change detection - create deep copies of arrays
    this.originalPortalForm = {
      ...this.portalForm,
      selectedProjectIds: [...this.portalForm.selectedProjectIds], // Create a copy of the array
      customLinks: [...this.portalForm.customLinks], // Create a copy of the array
      featuredCollections: [...this.portalForm.featuredCollections], // Create a copy of the array
      collections: [...this.portalForm.collections], // Create a copy of the array
      homepageMetadata: this.portalForm.homepageMetadata
        ? { ...this.portalForm.homepageMetadata }
        : { title: '', description: '' }, // Create a copy of the object
      loginMethods: this.portalForm.loginMethods ? JSON.parse(JSON.stringify(this.portalForm.loginMethods)) : undefined,
    };

    // Fetch collections for the new portal
    await this.getCollections();

    // Update the precalculated featured collections
    this.updateFeaturedCollections();
  }

  async cancelPortalForm() {
    this.isCreatingPortal = false;

    // Restore the original form state
    this.restoreOriginalForm();

    // If there's no portal, automatically start portal creation again
    if (!this.portal) {
      await this.startCreatePortal();
    }
  }

  async submitPortalForm() {
    // Validate domain only if provided
    if (this.portalForm.customDomain) {
      const domainError = this.validateDomain(this.portalForm.customDomain);
      if (domainError) {
        this.msgSrvc.showError(domainError);
        return;
      }
    }
    // Validate logo is required
    if (!this.portalForm.supportLogo) {
      this.msgSrvc.showError('Please upload a logo for your portal');
      return;
    }
    if (!this.portalForm.selectedProjectIds.length) {
      this.msgSrvc.showError('Please select at least one project');
      return;
    }
    // Validate Slack channel ID is required
    if (!this.portalForm.slackChannelId || this.portalForm.slackChannelId.trim() === '') {
      this.msgSrvc.showError('Please select a Slack channel');
      return;
    }

    // Validate that a Slack channel is selected
    if (!this.selectedSlackChannel) {
      this.msgSrvc.showError('Please select a Slack channel');
      return;
    }

    // Validate that ALL enabled projects have at least one ticket type before creating/updating portal
    const projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(this.portalForm.selectedProjectIds);
    if (projectsWithoutTicketTypes.length > 0) {
      const projectList = projectsWithoutTicketTypes
        .map((project) => `<li><strong>${project.name}</strong></li>`)
        .join('');

      const alert = await this.alertCtrl.create({
        header: 'Cannot Create Portal',
        message: `
          <div style="text-align: left;">
            <p><strong>All enabled projects must have at least one ticket type before you can create or update the portal.</strong></p>
            <p>The following projects don't have any ticket types:</p>
            <ul style="margin: 8px 0; padding-left: 20px;">
              ${projectList}
            </ul>
            <p>Please create ticket types for these projects first, then try again.</p>
          </div>
        `,
        buttons: [
          {
            text: 'OK',
            role: 'cancel',
          },
          {
            text: 'Go to Ticket Types',
            handler: () => {
              // Navigate to the first project's ticket types page
              if (projectsWithoutTicketTypes.length > 0) {
                this.navigateToTicketTypes(projectsWithoutTicketTypes[0].id);
              }
            },
          },
        ],
      });
      await alert.present();
      return;
    }

    this.isLoading = true;
    try {
      const portalData: any = {
        isExternal: this.config.isExternal,
        isEnabled: this.portalForm.isEnabled,
        projectIds: this.portalForm.selectedProjectIds,
        customLinks: this.portalForm.customLinks,
        featuredCollections: this.portalForm.featuredCollections,
        collections: this.portalForm.collections,
        settings: {
          logoUrl: this.portalForm.supportLogo || '',
          primaryColor: this.portalForm.primaryColor,
          darkThemeAccentColor: this.portalForm.darkThemeAccentColor,
          allowDirectTicketCreation: this.portalForm.allowDirectTicketCreation,
        },
        loginMethods: this.portalForm.loginMethods,
        slackChannelId: this.portalForm.slackChannelId,
        homepageMetadata: this.portalForm.homepageMetadata,
      };

      // Always include customDomain, even if empty (to allow clearing the field)
      portalData.customDomain =
        this.portalForm.customDomain && this.portalForm.customDomain.trim()
          ? this.portalForm.customDomain.trim()
          : null;

      if (this.portal) {
        const updatedPortal = await this.portalsService.update(this.portal.id, portalData);
        this.portal = updatedPortal;
        this.msgSrvc.show('Portal updated successfully');
        // Re-initialize the form with the updated portal data from the server
        await this.initializePortalForm(updatedPortal);
        // Force form object reference change
        this.portalForm = { ...this.portalForm };
        // Dismiss unsaved changes toast since changes are now saved
        this.dismissUnsavedChangesToast();
      } else {
        // If no portal exists, create a new one
        const newPortal = await this.portalsService.create(portalData);
        this.portal = newPortal;
        this.isCreatingPortal = false; // Exit creation mode
        this.msgSrvc.show('Portal created successfully');
        // Refresh portal data to ensure we have the latest information
        await this.getPortal();
        // Dismiss unsaved changes toast since changes are now saved
        this.dismissUnsavedChangesToast();
      }
      // Don't call cancelPortalForm here as we want to keep the current state
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }

  async saveAuthentication() {
    if (!this.portal) {
      this.msgSrvc.showError('No portal selected');
      return;
    }

    try {
      this.isSavingAuthentication = true;

      // if sso is disabled, remove the sso object from the array
      if (!this.portalForm.loginMethods?.some((method) => method.type === 'sso' && method.enabled)) {
        this.portalForm.loginMethods = this.portalForm.loginMethods?.filter((method) => method.type !== 'sso');
      }

      // Validate that at least one login method is enabled
      if (!this.portalForm.loginMethods || !this.portalForm.loginMethods.some((method) => method.enabled)) {
        this.msgSrvc.showError('Please enable at least one authentication method (Email or SSO)');
        return;
      }

      // Validate SSO configuration if SSO is enabled
      const ssoMethod = this.portalForm.loginMethods.find((method) => method.type === 'sso' && method.enabled);
      if (ssoMethod) {
        const ssoConfig = ssoMethod.sso;
        if (!ssoConfig) {
          this.msgSrvc.showError('SSO configuration is incomplete. Please provide all SSO fields.');
          return;
        }

        const { clientId, issuerUrl, clientSecret } = ssoConfig;
        if (!clientId || !clientId.trim()) {
          this.msgSrvc.showError('SSO Client ID is required');
          return;
        }

        if (!issuerUrl || !issuerUrl.trim()) {
          this.msgSrvc.showError('SSO Issuer URL is required');
          return;
        }

        // Validate URL format
        try {
          const url = new URL(issuerUrl.trim());
          if (!['http:', 'https:'].includes(url.protocol)) {
            this.msgSrvc.showError('SSO Issuer URL must be a valid HTTP or HTTPS URL');
            return;
          }
        } catch (urlError) {
          this.msgSrvc.showError('SSO Issuer URL must be a valid URL');
          return;
        }

        if (!clientSecret || !clientSecret.trim()) {
          this.msgSrvc.showError('SSO Client Secret is required');
          return;
        }
      }

      // Validate and include signInEmailSender
      let signInEmailSender = null;

      if (this.portalForm.signInEmailSender && this.portalForm.signInEmailSender.trim()) {
        const trimmedEmail = this.portalForm.signInEmailSender.trim();

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(trimmedEmail)) {
          this.msgSrvc.showError('Please enter a valid email address');
          return;
        }

        // Validate that email comes from a verified domain
        if (!this.isEmailFromVerifiedDomain(trimmedEmail)) {
          this.msgSrvc.showError('The email domain must be verified in your email domain settings');
          return;
        }

        signInEmailSender = trimmedEmail;
      }

      const authenticationData: any = {
        loginMethods: this.portalForm.loginMethods,
      };

      // Include signInEmailSender if custom, or set to null to clear it
      authenticationData.signInEmailSender = signInEmailSender;

      const updatedPortal = await this.portalsService.update(this.portal.id, authenticationData);
      this.portal = updatedPortal;
      this.msgSrvc.show('Authentication settings saved successfully');

      // Re-initialize the form with the updated portal data
      await this.initializePortalForm(updatedPortal);
      this.portalForm = { ...this.portalForm };
    } catch (err) {
      const errorMessage =
        typeof err === 'string'
          ? err
          : err?.error?.errors?.[0].title ?? err.message ?? 'Something went wrong. Please try again.';
      this.msgSrvc.show(errorMessage, {
        duration: 0, // Don't auto-dismiss - stay until user clicks close
        buttons: [
          {
            text: '✕',
            role: 'cancel',
          },
        ],
      });
    } finally {
      this.isSavingAuthentication = false;
    }
  }

  revertAuthenticationChanges() {
    if (!this.originalPortalForm) return;

    // Revert login methods
    this.portalForm.loginMethods = this.originalPortalForm.loginMethods
      ? JSON.parse(JSON.stringify(this.originalPortalForm.loginMethods))
      : undefined;

    // Revert sign in email sender
    this.portalForm.signInEmailSender = this.originalPortalForm.signInEmailSender;

    // Revert email sender UI components
    if (this.portalForm.signInEmailSender?.trim()) {
      const email = this.portalForm.signInEmailSender.trim();
      const atIndex = email.indexOf('@');
      if (atIndex > 0) {
        this.signInEmailPrefix = email.substring(0, atIndex);
        this.selectedEmailDomain = email.substring(atIndex + 1);
      }
    } else {
      this.signInEmailPrefix = '';
      this.selectedEmailDomain = '';
    }

    // Dismiss unsaved changes toast since changes are reverted
    this.dismissUnsavedChangesToast();
  }

  // Clear custom domain field
  async clearCustomDomain() {
    const alert = await this.alertCtrl.create({
      header: 'Clear Custom Domain',
      message:
        'Are you sure you want to remove the custom domain? This will make the portal accessible only via the default domain.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Clear Domain',
          handler: async () => {
            this.portalForm.customDomain = '';
            // Force change detection by updating the form object reference
            this.portalForm = { ...this.portalForm };

            // Automatically save the change
            await this.submitPortalForm();
          },
        },
      ],
    });
    await alert.present();
  }

  // Handle custom domain input changes to prevent whitespace
  onCustomDomainInput(event: any) {
    const value = event.target.value;
    // Remove all whitespace (leading, trailing, and internal)
    const trimmedValue = value.replace(/\s/g, '');

    // If the trimmed value is different, update the form
    if (value !== trimmedValue) {
      // Use setTimeout to update after Angular's change detection cycle
      setTimeout(() => {
        this.portalForm.customDomain = trimmedValue;
        // Force change detection
        this.portalForm = { ...this.portalForm };
      }, 0);
    }
  }

  // Shared logo upload logic
  async uploadPortalLogo() {
    try {
      const modal = await this.modalCtrl.create({
        component: ImageModalComponent,
        componentProps: {
          title: 'Upload Logo',
          description: 'Upload a logo for your portal',
          aspectRatio: 1,
          maxWidth: 512,
          maxHeight: 512,
        },
      });
      await modal.present();
      const { data } = await modal.onWillDismiss();
      if (data && data.uploadFile) {
        this.isUploadingLogo = true;
        try {
          const uploadResult = await this.uploadService.upload(
            [data.uploadFile],
            'portal-logos',
            fileNameFromImageModalUpload(data.uploadFile),
          );
          this.portalForm.supportLogo = uploadResult[0].url;
        } catch (uploadError) {
          throw uploadError;
        } finally {
          this.isUploadingLogo = false;
        }
      }
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  getProjectName(projectId: string): string {
    const project = this.projects.find((p) => p.id === projectId);
    return project?.name || 'Unknown Project';
  }

  // Check if there are no projects for the current portal type
  get hasNoProjectsForPortalType(): boolean {
    return this.projects.length === 0;
  }

  // Get the project type name for the current portal
  get projectTypeName(): string {
    return this.config.isExternal ? 'external' : 'internal';
  }

  // Make window available in template
  get window(): Window {
    return window;
  }

  // Initialize login methods with defaults if missing
  private initializeLoginMethods(loginMethods: any) {
    if (!loginMethods || !Array.isArray(loginMethods)) {
      return [
        { type: 'magicLink', enabled: true },
        {
          type: 'sso',
          enabled: false,
          sso: {
            name: '',
            clientId: '',
            issuerUrl: '',
            clientSecret: '',
          },
        },
      ];
    }

    // Ensure both methods exist
    const methods: any[] = [];

    // Ensure magicLink exists
    let magicLink = loginMethods.find((m: any) => m.type === 'magicLink');
    if (!magicLink) {
      magicLink = { type: 'magicLink', enabled: true };
    }
    methods.push(magicLink);

    // Ensure sso exists
    let sso = loginMethods.find((m: any) => m.type === 'sso');
    if (!sso) {
      sso = {
        type: 'sso',
        enabled: false,
        sso: {
          name: '',
          clientId: '',
          issuerUrl: '',
          clientSecret: '',
        },
      };
    } else if (!sso.sso) {
      // Ensure sso property exists even if method exists
      sso.sso = {
        name: '',
        clientId: '',
        issuerUrl: '',
        clientSecret: '',
      };
    }
    methods.push(sso);

    return methods;
  }

  // Helper methods for login methods
  getLoginMethod(type: 'magicLink' | 'sso') {
    return this.portalForm.loginMethods?.find((method) => method.type === type);
  }

  setLoginMethodEnabled(type: 'magicLink' | 'sso', enabled: boolean) {
    if (!this.portalForm.loginMethods) return;
    const method = this.portalForm.loginMethods.find((m) => m.type === type);
    if (method) {
      method.enabled = enabled;
      // Ensure sso object exists when SSO is enabled
      if (type === 'sso' && enabled && !method.sso) {
        method.sso = {
          name: '',
          clientId: '',
          issuerUrl: '',
          clientSecret: '',
        };
      }
    }
  }

  getSsoConfig() {
    const ssoMethod = this.getLoginMethod('sso');
    return ssoMethod?.sso;
  }

  // Getters and setters for SSO fields (needed for two-way binding)
  get ssoClientId(): string {
    return this.getSsoConfig()?.clientId || '';
  }

  set ssoClientId(value: string) {
    const ssoConfig = this.getSsoConfig();
    if (ssoConfig) {
      ssoConfig.clientId = value;
    }
  }

  get ssoIssuerUrl(): string {
    return this.getSsoConfig()?.issuerUrl || '';
  }

  set ssoIssuerUrl(value: string) {
    const ssoConfig = this.getSsoConfig();
    if (ssoConfig) {
      ssoConfig.issuerUrl = value;
    }
  }

  get ssoClientSecret(): string {
    return this.getSsoConfig()?.clientSecret || '';
  }

  set ssoClientSecret(value: string) {
    const ssoConfig = this.getSsoConfig();
    if (ssoConfig) {
      ssoConfig.clientSecret = value;
    }
  }

  toggleSsoSecretVisibility() {
    this.showSsoSecret = !this.showSsoSecret;
  }

  // Validate that selected projects have at least one ticket type
  async validateProjectsHaveTicketTypes(projectIds: string[]): Promise<boolean> {
    if (!projectIds || projectIds.length === 0) {
      return false;
    }

    try {
      // Check each project for ticket types
      for (const projectId of projectIds) {
        const ticketTypesResponse = await this.ticketTypesService.list({
          limit: 1, // We only need to know if there's at least one
          where: [
            {
              field: 'projectId',
              operator: '==',
              value: projectId,
            },
          ],
        });

        if (ticketTypesResponse.data.length > 0) {
          // Found at least one ticket type for one of the projects
          return true;
        }
      }

      // No ticket types found for any of the selected projects
      return false;
    } catch (error) {
      // If there's an error, we'll assume validation passes to avoid blocking users
      return true;
    }
  }

  // Check which projects don't have ticket types and return project details
  async getProjectsWithoutTicketTypes(projectIds: string[]): Promise<{ id: string; name: string }[]> {
    if (!projectIds || projectIds.length === 0) {
      return [];
    }

    const projectsWithoutTicketTypes: { id: string; name: string }[] = [];

    try {
      for (const projectId of projectIds) {
        const ticketTypesResponse = await this.ticketTypesService.list({
          limit: 1, // We only need to know if there's at least one
          where: [
            {
              field: 'projectId',
              operator: '==',
              value: projectId,
            },
          ],
        });

        if (ticketTypesResponse.data.length === 0) {
          // Find project name
          const project = this.projects.find((p) => p.id === projectId);
          if (project) {
            projectsWithoutTicketTypes.push({
              id: projectId,
              name: project.name || 'Unknown Project',
            });
          }
        }
      }
    } catch (error) {
      // If there's an error, return empty array to avoid blocking users
      console.error('Error checking ticket types:', error);
    }

    return projectsWithoutTicketTypes;
  }

  // Navigate to ticket types page for a specific project
  navigateToTicketTypes(projectId: string) {
    this.navCtrl.navigateRoot(`/dashboard/settings/projects/${projectId}/ticket-types`);
  }

  // Get projects without ticket types for warning display (only for selected projects)
  getProjectsWithoutTicketTypesWarning(): { id: string; name: string }[] {
    return this.projectsWithoutTicketTypes;
  }

  // Check if a specific project has no ticket types
  hasProjectNoTicketTypes(projectId: string): boolean {
    return this.projectsWithoutTicketTypes.some((p) => p.id === projectId);
  }

  // Check if a project can be selected (has ticket types)
  canProjectBeSelected(projectId: string): boolean {
    return !this.hasProjectNoTicketTypes(projectId);
  }

  // Navigate to project creation
  navigateToProjectCreation() {
    this.navCtrl.navigateRoot('/dashboard/settings/project-list');
  }

  // Navigate to email domain verification
  navigateToEmailDomainVerification() {
    this.navCtrl.navigateRoot('/dashboard/settings/security');
  }

  // Load verified email domains for validation
  async loadEmailDomains() {
    try {
      const emailDomainsResponse: any = await this.apiService.postPromise('/email-domains/list', {});
      const domains = emailDomainsResponse.data ?? [];
      this.verifiedEmailDomains = domains.filter((d) => d.valid);
    } catch (error) {
      console.error('Error loading email domains:', error);
      this.verifiedEmailDomains = [];
    }
  }

  // Validate that the email comes from a verified domain
  isEmailFromVerifiedDomain(email: string): boolean {
    if (!email || !this.verifiedEmailDomains.length) {
      return false;
    }

    const emailDomain = email.split('@')[1];
    if (!emailDomain) {
      return false;
    }

    return this.verifiedEmailDomains.some((domain) => domain.domain === emailDomain);
  }

  // Get the sender email for display
  getSenderEmail(): string {
    // get from portal settings, not from the form
    if (this.portal?.signInEmailSender?.trim()) {
      return this.portal.signInEmailSender.trim();
    }
    return 'noreply@mail.unthread.io';
  }

  // Handle email prefix change - fixed TypeScript issue
  onEmailPrefixChange(event: CustomEvent) {
    const value = (event.target as HTMLIonInputElement).value;
    this.signInEmailPrefix = typeof value === 'string' ? value.trim() : '';
    this.updateSignInEmailSender();
  }

  // Handle email domain selection change
  onEmailDomainChange() {
    this.selectedEmailDomain = this.selectedEmailDomain.trim();
    this.updateSignInEmailSender();
  }

  // Update the signInEmailSender based on prefix and domain
  updateSignInEmailSender() {
    if (this.signInEmailPrefix?.trim() && this.selectedEmailDomain?.trim()) {
      this.portalForm.signInEmailSender = `${this.signInEmailPrefix.trim()}@${this.selectedEmailDomain.trim()}`;
    } else {
      this.portalForm.signInEmailSender = '';
    }
  }

  // Clear the custom email sender
  clearCustomEmailSender() {
    this.signInEmailPrefix = '';
    this.selectedEmailDomain = '';
    this.portalForm.signInEmailSender = '';
  }

  // Generate full portal URL with https protocol
  generatePortalUrl(portal: Partial<Portal>): string {
    if (portal.customDomain) {
      return 'https://' + portal.customDomain;
    }

    if (environment.environment === 'dev') {
      return portal.isExternal ? 'http://external.localhost:3000' : 'http://internal.localhost:3000';
    }

    return 'https://' + (portal as any).defaultDomain;
  }

  // Test DNS connection for custom domain
  async testDnsConnection(portal: Partial<Portal>) {
    if (!portal.customDomain) {
      this.msgSrvc.show('No custom domain configured');
      return;
    }

    this.isDnsTesting = true;
    this.currentTestingPortal = portal as Portal;
    this.currentDnsStep = 0;

    try {
      // Simulate DNS test steps with delays
      for (let i = 0; i < this.dnsTestSteps.length; i++) {
        this.currentDnsStep = i;
        await new Promise((resolve) => setTimeout(resolve, 800));
      }

      const result = await this.apiService.postPromise<{
        success: boolean;
        message?: string;
        reason?: string;
        cnameRecords?: string[];
        expectedTarget?: string;
        error?: string;
      }>('/portals/test-dns', {
        customDomain: portal.customDomain,
        portalId: portal.id,
      });

      if (result.success) {
        this.msgSrvc.show('✅ DNS connection successful! Your custom domain is properly configured.');
      } else {
        this.msgSrvc.show(result.message || '❌ DNS connection failed. Please check your CNAME record configuration.', {
          duration: 0,
          buttons: [
            {
              text: '✕',
              role: 'cancel',
              handler: () => {
                // Toast will be dismissed automatically
              },
            },
          ],
        });
      }
    } catch (error: any) {
      if (error.status === 403) {
        this.msgSrvc.show('⚠️ Access denied. Please try refreshing the page and try again.');
      } else {
        this.msgSrvc.show('⚠️ Connection test failed. Please verify your DNS settings manually.');
      }
    } finally {
      this.isDnsTesting = false;
      this.currentTestingPortal = null;
      this.currentDnsStep = 0;
    }
  }

  // Test TLS certificate for custom domain
  async testTlsCertificate(portal: Partial<Portal>) {
    if (!portal.customDomain) {
      this.msgSrvc.show('No custom domain configured');
      return;
    }

    this.isTlsTesting = true;
    this.currentTestingPortal = portal as Portal;
    this.currentTlsStep = 0;

    try {
      // Simulate TLS test steps with delays
      for (let i = 0; i < this.tlsTestSteps.length; i++) {
        this.currentTlsStep = i;
        await new Promise((resolve) => setTimeout(resolve, 600));
      }

      const result = await this.apiService.postPromise<{
        success: boolean;
        message?: string;
        exists?: boolean;
        status?: string;
        sslStatus?: string;
        error?: string;
      }>('/portals/check-tls', {
        customDomain: portal.customDomain,
        portalId: portal.id,
      });

      if (result.success) {
        this.msgSrvc.show('✅ TLS certificate is valid and ready! Your portal can be published securely.');
      } else {
        let message = '❌ TLS certificate is not ready.';
        let isError = true;

        if (!result.exists) {
          message =
            '❌ TLS certificate has not been created yet. This usually takes a few minutes after DNS is configured.';
        } else if (result.error && result.error.includes('CNAME Configuration Error')) {
          // Show specific CNAME error message
          message = result.error;
        } else if (result.status !== 'active' || result.sslStatus !== 'active') {
          message = `🔄 TLS certificate is still being provisioned. Current status: Hostname ${result.status}, SSL ${result.sslStatus}.`;
          isError = false; // This is not an error, just in progress
        }

        if (isError) {
          this.msgSrvc.show(message, {
            duration: 0,
            buttons: [
              {
                text: '✕',
                role: 'cancel',
                handler: () => {
                  // Toast will be dismissed automatically
                },
              },
            ],
          });
        } else {
          this.msgSrvc.show(message); // Auto-dismiss for in-progress messages
        }
      }
    } catch (error: any) {
      if (error.status === 403) {
        this.msgSrvc.show('⚠️ Access denied. Please try refreshing the page and try again.');
      } else {
        this.msgSrvc.show('⚠️ TLS certificate check failed. Please try again later.');
      }
    } finally {
      this.isTlsTesting = false;
      this.currentTestingPortal = null;
      this.currentTlsStep = 0;
    }
  }

  // Generic copy to clipboard method
  async copyToClipboard(text: string, label?: string) {
    try {
      await navigator.clipboard.writeText(text);
      this.msgSrvc.show(label ? `${label} copied to clipboard` : 'Copied to clipboard');
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.msgSrvc.show(label ? `${label} copied to clipboard` : 'Copied to clipboard');
    }
  }

  async deletePortal(id: string) {
    const alert = await this.alertCtrl.create({
      header: 'Delete Portal',
      message: 'Are you sure you want to delete this portal? This action cannot be undone.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: async () => {
            this.isLoading = true;
            try {
              await this.portalsService.delete(id);
              this.portal = null; // Remove the portal from local state
              await this.startCreatePortal(); // start portal creation now that the existing portal has been deleted

              this.msgSrvc.show('Portal deleted successfully');
            } catch (err) {
              this.msgSrvc.showError(err);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async publishPortal(id: string) {
    if (this.portal?.id !== id) return;
    const portal = this.portal;
    const customDomain = portal.customDomain || '';

    // Check if no custom domain is set and proceed directly without modal
    if (!customDomain || customDomain.trim() === '') {
      // Continue with publish process without custom domain
      await this.continuePublishWithoutCustomDomain(id, portal);
      return;
    }

    // Continue with normal publish process for portals with custom domain
    await this.continuePublishWithCustomDomain(id, portal);
  }

  private async continuePublishWithoutCustomDomain(id: string, portal: Partial<Portal>) {
    // Start publishing process with step tracking
    this.isPublishing = true;
    this.currentTestingPortal = portal.id ? (portal as Portal) : null;
    this.currentPublishStep = 0;

    try {
      // Step 1: Validate ticket types
      this.currentPublishStep = 0;
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (portal.projectIds && portal.projectIds.length > 0) {
        const projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(portal.projectIds);
        if (projectsWithoutTicketTypes.length > 0) {
          this.isPublishing = false;
          this.currentTestingPortal = null;
          this.currentPublishStep = 0;

          const projectList = projectsWithoutTicketTypes
            .map((project) => `<li><strong>${project.name}</strong></li>`)
            .join('');

          const alert = await this.alertCtrl.create({
            header: 'Cannot Publish Portal',
            message: `
              <div style="text-align: left;">
                <p><strong>All enabled projects must have at least one ticket type before you can publish the portal.</strong></p>
                <p>The following projects don't have any ticket types:</p>
                <ul style="margin: 8px 0; padding-left: 20px;">
                  ${projectList}
                </ul>
                <p>Please create ticket types for these projects first, then try again.</p>
              </div>
            `,
            buttons: [
              {
                text: 'OK',
                role: 'cancel',
              },
              {
                text: 'Go to Ticket Types',
                handler: () => {
                  // Navigate to the first project's ticket types page
                  if (projectsWithoutTicketTypes.length > 0) {
                    this.navigateToTicketTypes(projectsWithoutTicketTypes[0].id);
                  }
                },
              },
            ],
          });

          await alert.present();
          return;
        }
      }

      // Step 2: Validate project configuration
      this.currentPublishStep = 1;
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Step 3: Verify portal requirements
      this.currentPublishStep = 2;
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Skip DNS and TLS checks for portals without custom domain
      // Step 4: Prepare to publish (skip DNS/TLS)
      this.currentPublishStep = 5;
      await new Promise((resolve) => setTimeout(resolve, 400));

      this.isPublishing = false;
      this.currentTestingPortal = null;
      this.currentPublishStep = 0;

      // Publish directly without confirmation
      this.isLoading = true;
      try {
        await this.portalsService.update(id, { isEnabled: true });
        if (this.portal) {
          this.portal.isEnabled = true;
        }
        this.msgSrvc.show('Portal published successfully');
      } catch (err) {
        this.msgSrvc.showError(err);
      } finally {
        this.isLoading = false;
      }
    } catch (error) {
      this.isPublishing = false;
      this.currentTestingPortal = null;
      this.currentPublishStep = 0;
      this.msgSrvc.showError('An unexpected error occurred during publishing.');
    }
  }

  private async continuePublishWithCustomDomain(id: string, portal: Partial<Portal>) {
    const customDomain = portal.customDomain || '';

    // Start publishing process with step tracking
    this.isPublishing = true;
    this.currentTestingPortal = portal.id ? (portal as Portal) : null;
    this.currentPublishStep = 0;

    try {
      // Step 1: Validate ticket types
      this.currentPublishStep = 0;
      await new Promise((resolve) => setTimeout(resolve, 800));

      if (portal.projectIds && portal.projectIds.length > 0) {
        const projectsWithoutTicketTypes = await this.getProjectsWithoutTicketTypes(portal.projectIds);
        if (projectsWithoutTicketTypes.length > 0) {
          this.isPublishing = false;
          this.currentTestingPortal = null;
          this.currentPublishStep = 0;

          const projectList = projectsWithoutTicketTypes
            .map((project) => `<li><strong>${project.name}</strong></li>`)
            .join('');

          const alert = await this.alertCtrl.create({
            header: 'Cannot Publish Portal',
            message: `
              <div style="text-align: left;">
                <p><strong>All enabled projects must have at least one ticket type before you can publish the portal.</strong></p>
                <p>The following projects don't have any ticket types:</p>
                <ul style="margin: 8px 0; padding-left: 20px;">
                  ${projectList}
                </ul>
                <p>Please create ticket types for these projects first, then try again.</p>
              </div>
            `,
            buttons: [
              {
                text: 'OK',
                role: 'cancel',
              },
              {
                text: 'Go to Ticket Types',
                handler: () => {
                  // Navigate to the first project's ticket types page
                  if (projectsWithoutTicketTypes.length > 0) {
                    this.navigateToTicketTypes(projectsWithoutTicketTypes[0].id);
                  }
                },
              },
            ],
          });

          await alert.present();
          return;
        }
      }

      // Step 2: Validate project configuration
      this.currentPublishStep = 1;
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Step 3: Verify portal requirements
      this.currentPublishStep = 2;
      await new Promise((resolve) => setTimeout(resolve, 600));

      // Step 4: Test DNS configuration
      this.currentPublishStep = 3;
      await new Promise((resolve) => setTimeout(resolve, 800));

      let dnsResult: { success: boolean; message?: string } | undefined;
      try {
        dnsResult = await this.apiService.postPromise<{
          success: boolean;
          message?: string;
          reason?: string;
          cnameRecords?: string[];
          expectedTarget?: string;
          error?: string;
        }>('/portals/test-dns', {
          customDomain: portal.customDomain,
          portalId: portal.id,
        });
      } catch (error: any) {
        this.isPublishing = false;
        this.currentTestingPortal = null;
        this.currentPublishStep = 0;
        await this.alertCtrl
          .create({
            header: 'DNS Test Failed',
            message: 'Could not verify DNS configuration. Please try again or check your DNS settings.',
            buttons: ['OK'],
          })
          .then((alert) => alert.present());
        return;
      }

      if (!dnsResult?.success) {
        this.isPublishing = false;
        this.currentTestingPortal = null;
        this.currentPublishStep = 0;
        await this.alertCtrl
          .create({
            header: 'Cannot Publish Portal',
            message: `
            <div style="text-align: left;">
              <strong>You cannot publish this portal until your DNS is properly configured.</strong><br><br>
              ${dnsResult?.message || `❌ CNAME Configuration Error: Your domain ${customDomain} does not have a CNAME record pointing to portal.${this.rootDomain}. Please add a CNAME record in your DNS settings.`}<br><br>
              <ul style="margin-top: 8px; margin-bottom: 8px;">
                <li>Make sure your subdomain <code>${customDomain}</code> points to <code>portal.${this.rootDomain}</code>.</li>
                <li>DNS changes may take up to 24 hours to propagate.</li>
                <li>After updating your DNS, try publishing again.</li>
              </ul>
            </div>
          `,
            buttons: ['OK'],
          })
          .then((alert) => alert.present());
        return;
      }

      // Step 5: Check TLS certificate status
      this.currentPublishStep = 4;
      await new Promise((resolve) => setTimeout(resolve, 600));

      let tlsResult:
        | {
            success: boolean;
            message?: string;
            exists?: boolean;
            status?: string;
            sslStatus?: string;
          }
        | undefined;
      try {
        tlsResult = await this.apiService.postPromise<{
          success: boolean;
          message?: string;
          exists?: boolean;
          status?: string;
          sslStatus?: string;
          error?: string;
        }>('/portals/check-tls', {
          customDomain: portal.customDomain,
          portalId: portal.id,
        });
      } catch (error: any) {
        this.isPublishing = false;
        this.currentTestingPortal = null;
        this.currentPublishStep = 0;
        await this.alertCtrl
          .create({
            header: 'TLS Certificate Check Failed',
            message: 'Could not verify TLS certificate status. Please try again.',
            buttons: ['OK'],
          })
          .then((alert) => alert.present());
        return;
      }

      if (!tlsResult?.success) {
        this.isPublishing = false;
        this.currentTestingPortal = null;
        this.currentPublishStep = 0;
        let tlsMessage = tlsResult?.message || 'TLS certificate is not ready';

        if (!tlsResult?.exists) {
          tlsMessage =
            'TLS certificate has not been created yet. This usually takes a few minutes after DNS is configured.';
        } else if (tlsResult?.message && tlsResult.message.includes('CNAME Configuration Error')) {
          // Show specific CNAME error message
          tlsMessage = tlsResult.message;
        } else if (tlsResult?.status !== 'active' || tlsResult?.sslStatus !== 'active') {
          tlsMessage = `TLS certificate is still being provisioned. Current status: Hostname ${tlsResult?.status}, SSL ${tlsResult?.sslStatus}. This can take up to 10 minutes.`;
        }

        await this.alertCtrl
          .create({
            header: 'Cannot Publish Portal',
            message: `
            <div style="text-align: left;">
              <strong>TLS certificate not ready.</strong><br><br>
              ${tlsMessage}<br><br>
              <div style="margin-top: 12px; padding: 8px; background: #f8f9fa; border-radius: 6px; border-left: 3px solid #007bff; font-size: 14px;">
                <strong>Note:</strong> Unthread handles TLS certificates for you. If the certificate is not ready in 10 minutes, <a href="mailto:jake@unthread.io" style="color: #007bff;">contact us</a> for assistance.
              </div>
            </div>
          `,
            buttons: ['OK'],
          })
          .then((alert) => alert.present());
        return;
      }

      // Step 6: Prepare to publish
      this.currentPublishStep = 5;
      await new Promise((resolve) => setTimeout(resolve, 400));

      this.isPublishing = false;
      this.currentTestingPortal = null;
      this.currentPublishStep = 0;

      // Publish directly without confirmation
      this.isLoading = true;
      try {
        await this.portalsService.update(id, { isEnabled: true });
        if (this.portal) {
          this.portal.isEnabled = true;
        }
        this.msgSrvc.show('Portal published successfully');
      } catch (err) {
        this.msgSrvc.showError(err);
      } finally {
        this.isLoading = false;
      }
    } catch (error) {
      this.isPublishing = false;
      this.currentTestingPortal = null;
      this.currentPublishStep = 0;
      this.msgSrvc.showError('An unexpected error occurred during publishing.');
    }
  }

  async disablePortal(id: string) {
    const alert = await this.alertCtrl.create({
      header: 'Disable Portal',
      message: 'Are you sure you want to disable this portal? Users will no longer be able to access it.',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Disable',
          role: 'destructive',
          handler: async () => {
            this.isLoading = true;
            try {
              await this.portalsService.update(id, { isEnabled: false });

              if (this.portal) {
                this.portal.isEnabled = false;
              }
            } catch (err) {
              this.msgSrvc.showError(err);
            } finally {
              this.isLoading = false;
            }
          },
        },
      ],
    });

    await alert.present();
  }

  async showLogoModal(logoUrl: string) {
    const alert = await this.alertCtrl.create({
      header: 'Portal Logo',
      message: `<div style="text-align: center; padding: 20px;">
      <img src="${logoUrl}" style="max-width: 100%; max-height: 60vh; width: auto; height: auto; object-fit: contain; border-radius: 8px;" />
    </div>`,
      buttons: ['Close'],
      cssClass: 'logo-modal',
    });

    await alert.present();
  }

  // Domain validation method
  validateDomain(domain: string): string {
    if (!domain) {
      return '';
    }

    // Remove protocol if present
    const cleanDomain = domain.replace(/^https?:\/\//, '');

    // Basic domain regex pattern
    const domainPattern =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/;

    if (!domainPattern.test(cleanDomain)) {
      const exampleDomain = this.tenant?.subdomain
        ? `support.${this.tenant?.subdomain}.com`
        : 'support.yourcompany.com';
      return `Please enter a valid domain (e.g., ${exampleDomain})`;
    }

    // Check for common invalid domains
    const invalidDomains = ['localhost', '127.0.0.1', '0.0.0.0'];
    if (invalidDomains.includes(cleanDomain.toLowerCase())) {
      return 'Please enter a valid domain name';
    }

    return '';
  }

  // Check if domain is valid (returns boolean)
  isValidDomain(domain: string): boolean {
    return this.validateDomain(domain) === '';
  }

  // Handle project checkbox changes
  async onProjectCheckboxChange(projectId: string, event: CustomEvent<{ checked: boolean }>) {
    // Prevent projects without ticket types from being selected
    if (this.hasProjectNoTicketTypes(projectId)) {
      return;
    }

    if (event.detail.checked) {
      if (!this.portalForm.selectedProjectIds.includes(projectId)) {
        this.portalForm.selectedProjectIds.push(projectId);
      }
    } else {
      this.portalForm.selectedProjectIds = this.portalForm.selectedProjectIds.filter((id) => id !== projectId);
    }

    // Refresh collections when projects change (for new portals)
    if (this.isCreatingPortal) {
      await this.getCollections();
    }
  }

  // Add custom link
  addCustomLink() {
    this.portalForm.customLinks.push({
      title: '',
      link: '',
      icon: 'link-outline',
    });
  }

  async selectCustomLinkIcon(index: number) {
    const popover = await this.popoverCtrl.create({
      component: IconPickerComponent,
      componentProps: {
        selectedIcon: this.portalForm.customLinks[index].icon,
        callback: this.updateCustomLinkIcon(index),
      },
    });

    popover.onDidDismiss().then((result) => {
      if (result.data) {
        this.updateCustomLinkIcon(index)(result.data);
      }
    });

    await popover.present();
  }

  // Remove custom link
  removeCustomLink(index: number) {
    this.portalForm.customLinks.splice(index, 1);
  }

  updateCustomLinkIcon(index: number) {
    return (icon: string) => {
      if (this.portalForm.customLinks[index]) {
        this.portalForm.customLinks[index].icon = icon;
      }
    };
  }

  // Impersonation functionality
  async openUserSelector(event: Event) {
    const popover = await this.popoverCtrl.create({
      component: SearchablePopoverComponent,
      componentProps: {
        loadData: async (searchText: string) => {
          return await this.loadUsersForImpersonation(searchText);
        },
        callback: (userId: string, userName: string, user: any) => {
          this.selectedImpersonateUser = user;
        },
        showImages: true,
        showSearch: true,
      },
      event: event,
    });

    await popover.present();
  }

  async impersonateSelectedUser(portalId: string) {
    if (!this.selectedImpersonateUser) {
      return;
    }
    await this.impersonateUser(portalId, this.selectedImpersonateUser.email);
  }

  async impersonateByEmail(portalId: string, event?: Event) {
    if (!this.impersonateEmail || !this.impersonateEmail.trim()) {
      this.msgSrvc.showError('Please enter an email address');
      return;
    }
    await this.impersonateUser(portalId, this.impersonateEmail.trim(), event);
  }

  async loadUsersForImpersonation(searchText: string): Promise<any[]> {
    try {
      const response = await this.apiService.postPromise<{ data: any[] }>('/users/list', {
        where: [
          {
            field: 'tenantId',
            operator: '==',
            value: this.authService.tenantId,
          },
          {
            field: 'isExternal',
            operator: '==',
            value: this.portal?.isExternal || false,
          },
          {
            field: 'isServiceAccount',
            operator: '==',
            value: false,
          },
          {
            field: 'deletedAt',
            operator: '==',
            value: null,
          },
          {
            field: 'email',
            operator: '!=',
            value: null,
          },
        ],
        limit: 100,
        order: ['name'],
      });

      const users = response.data || [];

      // Filter by search text
      const filteredUsers = searchText
        ? users.filter(
            (user) =>
              user.name?.toLowerCase().includes(searchText.toLowerCase()) ||
              user.email?.toLowerCase().includes(searchText.toLowerCase()),
          )
        : users;

      // Transform to SearchablePopover Item format
      return filteredUsers.map((user) => ({
        id: user.id,
        title: user.name || user.email || 'Unknown User',
        image: user.photo,
        object: user,
      }));
    } catch (error) {
      console.error('Failed to load users:', error);
      return [];
    }
  }

  async impersonateUser(portalId: string, email: string, event?: Event) {
    // Stop event propagation to prevent triggering other elements
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    this.isLoading = true;
    try {
      // Generate Firebase custom token for impersonation (tenant ID comes from auth)
      const result = await this.apiService.postPromise<{ data: string }>(`/portals/${portalId}/impersonate-user`, {
        email,
      });

      const authToken = result.data;

      // Generate portal URL
      const portalUrl = this.generatePortalUrl(this.portal!);

      // Use postMessage pattern (same as super admin impersonation)
      // Open the portal impersonation page
      window.open(`${portalUrl}/login/impersonate`, '_blank');

      // Send the custom token via postMessage when the portal requests it
      this.authService.sendCustomToken(authToken, portalUrl);

      this.msgSrvc.show(`Successfully impersonating ${email}`);
    } catch (err) {
      this.msgSrvc.showError(err);
    } finally {
      this.isLoading = false;
    }
  }
}
